import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import BrandSportNav from '../components/BrandSportNav';
import CollectionHero from '../components/CollectionHero';
import ProductCard from '../components/ProductCard';
import ProductFilters from '../components/ProductFilters';
import { StoreEmpty, StoreError, StoreLoading } from '../components/StoreState';
import { useProducts } from '../hooks/useProducts';
import { useBrands } from '../hooks/useBrands';
import {
  brandHeaderKey,
  fetchCollectionHeaders,
  getCollectionHeader,
} from '../lib/collectionHeaders';
import {
  findBrandBySlug,
  getSportsForBrand,
  productBelongsToBrand,
} from '../utils/brands';
import { getProductImageUrl } from '../utils/imageUrl';
import { parseFilterParams, productMatchesFilters } from '../utils/filters';
import { getSportBySlug, productBelongsToSport } from '../utils/sports';
import './Brand.css';

const BRAND_TONES = {
  mercedes: '#1a1a1a',
  puma: '#1c1814',
  adidas: '#141820',
  nike: '#181818',
  ferrari: '#241010',
  mclaren: '#1a1810',
  'red-bull': '#141428',
};

function Brand() {
  const { brandSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilterParams(searchParams), [searchParams]);
  const { products, loading, error } = useProducts();
  const { brands, loading: brandsLoading } = useBrands();
  const [collectionHeaders, setCollectionHeaders] = useState({});

  const sportSlug = searchParams.get('sport') || '';
  const selectedSport = getSportBySlug(sportSlug);
  const brand = useMemo(
    () => findBrandBySlug(brandSlug, brands),
    [brandSlug, brands]
  );

  useEffect(() => {
    let cancelled = false;
    fetchCollectionHeaders()
      .then(headers => {
        if (!cancelled) setCollectionHeaders(headers);
      })
      .catch(() => {
        if (!cancelled) setCollectionHeaders({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!brand) return;
    if (sportSlug && !selectedSport) {
      const next = new URLSearchParams(searchParams);
      next.delete('sport');
      setSearchParams(next, { replace: true });
    }
  }, [brand, sportSlug, selectedSport, searchParams, setSearchParams]);

  const brandProducts = useMemo(() => {
    if (!brandSlug) return [];
    return products.filter(product => productBelongsToBrand(product, brandSlug));
  }, [products, brandSlug]);

  const brandSports = useMemo(
    () => getSportsForBrand(products, brandSlug),
    [products, brandSlug]
  );

  const cover = useMemo(() => {
    const uploaded = getCollectionHeader(collectionHeaders, brandHeaderKey(brandSlug));
    if (uploaded) {
      return { imageUrl: uploaded.image_url, crop: uploaded.header_crop };
    }

    const first = brandProducts.find(product => getProductImageUrl(product));
    return { imageUrl: first ? getProductImageUrl(first) : null };
  }, [collectionHeaders, brandSlug, brandProducts]);

  const filteredProducts = useMemo(() => {
    let result = brandProducts;

    if (selectedSport) {
      result = result.filter(product =>
        productBelongsToSport(product, selectedSport)
      );
    }

    return result.filter(product => productMatchesFilters(product, filters));
  }, [brandProducts, selectedSport, filters]);

  if (!loading && !brandsLoading && !brand) {
    return (
      <main className="brand-page">
        <StoreEmpty
          eyebrow="Brands"
          title="Brand not found"
          lede="That brand is not in the house yet."
          actionLabel="Back home"
          actionTo="/"
        />
      </main>
    );
  }

  const title = brand?.name || 'Brand';

  return (
    <main className="brand-page">
      <CollectionHero
        title={title}
        imageUrl={cover.imageUrl}
        crop={cover.crop}
        tone={BRAND_TONES[brandSlug] || '#1a1a1a'}
      />

      <div className="brand-body">
        <div className="brand-layout">
          <ProductFilters />

          <div className="brand-results">
            <BrandSportNav sports={brandSports} brandSlug={brandSlug} />

            {loading && <StoreLoading label="Loading collection…" skeletons={6} />}
            {error && <StoreError message={error} />}

            {!loading && !error && !filteredProducts.length && (
              <StoreEmpty
                eyebrow={title}
                title="No pieces match"
                lede="Try clearing filters or choose another sport."
                actionLabel="Clear filters"
                actionTo={`/brands/${brandSlug}`}
              />
            )}

            {!loading && filteredProducts.length > 0 && (
              <div className="product-card-grid">
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

export default Brand;
